/**
 * The age band, as a word.
 *
 * ## Why this table is on the client when the genre table is not
 *
 * Row titles and facet labels are rendered server-side, because they are data-dependent:
 * the set of genres is whatever the classifier found in *this* catalogue, and a second
 * copy of that table on the client is a second thing to keep in step with it. Maturity is
 * not like that. It is a closed five-value enum in the wire contract, it changes only when
 * the contract changes, and it is a UI string in exactly the way "Devam et" is one.
 *
 * ## Why the labels are words and not numbers
 *
 * The obvious mapping is `teen → 13+`, `adult → 18+`, and it would be a small lie. The
 * classifier reaches those bands two ways: from a real rating it recognised — `PG-13`,
 * `TV-MA` — and from heuristics over a category name. A number states a certification this
 * catalogue does not have for most of its items. The provider's own rating string is
 * shown alongside where one exists, and that one *is* a number, honestly.
 *
 * ## Why `kids`, `general` and `unknown` return null
 *
 * A badge is a caution. Marking a cartoon "Çocuk" tells nobody anything they cannot see
 * from the artwork, and `unknown` — which is most of the catalogue, because enrichment is
 * deliberately slow — would put a shrug on almost every card. §17 of the plan: filtered
 * content is invisible, and unfiltered content is not decorated.
 */
import type { Maturity } from '@filmodasi/shared';

const LABELS: Readonly<Partial<Record<Maturity, string>>> = Object.freeze({
  teen: 'Gençler',
  adult: 'Yetişkin',
});

/** The badge text, or null where there is nothing worth flagging. */
export function maturityLabel(maturity: Maturity | undefined): string | null {
  return (maturity && LABELS[maturity]) ?? null;
}
